/** @format */
import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { ValidatablePlan } from 'common-typescript';
import { CreditRange, Module, OtmId } from 'common-typescript/types';
import { combineLatest, Observable, shareReplay } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppErrorHandler } from 'sis-components';
import { FirstLevelModuleService } from '../service/first-level-module.service';
import { SelectedValidatablePlanService } from '../service/selected-validatable-plan.service';
import { CreditSum, CreditSumService } from './credit-sum.service';
import { ModuleLabelService } from './module-label.service';

@Component({
    selector: 'app-total-credits',
    standalone: false,
    templateUrl: './total-credits.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ModuleLabelService, CreditSumService],
})
export class TotalCreditsComponent {
    /**
     * Emits total credits for {@link FirstLevelModuleService.firstLevelModules$}.
     */
    protected readonly firstLevelModuleTotals$: Observable<readonly ModuleTotal[]>;

    constructor(
        selectedValidatablePlanService: SelectedValidatablePlanService,
        firstLevelModuleService: FirstLevelModuleService,
        creditSumService: CreditSumService,
        moduleLabelService: ModuleLabelService,
        appErrorHandler: AppErrorHandler,
    ) {
        this.firstLevelModuleTotals$ = combineLatest([
            selectedValidatablePlanService.selectedValidatablePlan$,
            firstLevelModuleService.firstLevelModules$,
        ]).pipe(
            map(([selectedValidatablePlan, firstLevelModules]: [ValidatablePlan | null, readonly Module[]]) =>
                selectedValidatablePlan
                    ? firstLevelModules.map(module => {
                          const creditSum: CreditSum = creditSumService.getCreditSum(selectedValidatablePlan, module);

                          return <ModuleTotal>{
                              moduleId: module.id,
                              moduleLabel: moduleLabelService.createModuleLabel(module),
                              timedCredits: creditSum.timedCredits,
                              attainedCredits: creditSum.attainedCredits,
                          };
                      })
                    : [],
            ),
            appErrorHandler.defaultErrorHandler(),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
    }
}

export interface ModuleTotal {
    readonly moduleId: OtmId;
    /**
     * Translated module name with possible target credits (of degree programme or study module).
     */
    readonly moduleLabel: string;

    /**
     * Summed credits of timed but not attained course units selected under a module.
     * The range always has a {@link CreditRange.min},
     * but can have null {@link CreditRange.max},
     * if any of the summed course units lacks the maximum.
     */
    readonly timedCredits: CreditRange;

    /**
     * Summed credits of attained course units selected under a module, whether timed or not.
     */
    readonly attainedCredits: number;
}
